import { NavLink } from '@remix-run/react'
import { Code } from '#app/components/typography/code.tsx'
import { H1 } from '#app/components/typography/h1.tsx'
import { H2 } from '#app/components/typography/h2.tsx'
import { H3 } from '#app/components/typography/h3.tsx'
import { H4 } from '#app/components/typography/h4.tsx'
import { P } from '#app/components/typography/p.tsx'
import { Kbd } from '#app/old_components/typography/kbd.tsx'
import { CodeBlock } from '#app/old_components/ui/code-block.tsx'
import { Icon } from '#app/old_components/ui/icon.tsx'

export type componentProps = {
	name: string
	shortName: string
	fileName: Lowercase<string>
	category: Lowercase<string>
	description: string[]
	features?: string[]
	keyboard?: {
		key: string
		description: string
	}[]
	usage: string
	parts: {
		name: string
		description: string
		props?: {
			name: string
			type: string
			default: string
			description: string
		}[]
		state?: {
			renderProp: string
			CSSSelector: string
			description: string
		}[]
		events?: {
			name: string
			type: string
			description: string
		}[]
		layout?: {
			name: string
			type: string
			description: string
		}[]
		accessibility?: {
			name: string
			type: string
			description: string
		}[]
	}[]
	styling?: string
}

export const Description = ({ name, description }: { name: componentProps['name']; description: componentProps['description'] }) => {
	return (
		<>
			<H1>{name}</H1>
			{description.map((text, i) => (
				<P key={i} className="mt-6 max-w-2xl">
					{text}
				</P>
			))}
		</>
	)
}

export const Features = ({ features }: { features: componentProps['features'] }) => {
	return features ?
			<>
				<H2>Features</H2>
				<ul className="text-muted-500 list-inside space-y-1">
					{features.map(feature => (
						<li key={feature} className="my-8 flex">
							<div>
								<Icon name="check" className="mr-5 inline-block h-9 w-9 rounded-full bg-green-500 text-white" />
							</div>
							<div>{feature}</div>
						</li>
					))}
				</ul>
			</>
		:	null
}

export const Keyboard = ({ keyboard }: { keyboard: componentProps['keyboard'] }) => {
	return keyboard ?
			<>
				<H2>Keyboard interactions</H2>
				<div className="ring-opacity-5 mt-4 overflow-hidden ring-1 shadow ring-black sm:rounded-lg">
					<table className="min-w-full divide-y divide-gray-300">
						<thead className="bg-gray-50">
							<tr>
								<th scope="col" className="py-3.5 pr-3 pl-4 text-left text-sm font-semibold text-gray-900 sm:pl-6">
									Prop
								</th>
								<th scope="col" className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
									Type
								</th>
								<th scope="col" className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
									Default
								</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-gray-200 bg-white">
							{keyboard.map(({ key, description }) => (
								<tr key={key}>
									<td className="whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium text-gray-900 sm:pl-6">
										<Kbd>{key}</Kbd>
									</td>
									<td className="whitespace-nowrap py-4 px-3 text-sm text-gray-500">{description}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</>
		:	null
}

export const Parts = ({ parts }: { parts?: componentProps['parts'] }) => {
	return (
		<>
			<H2>Parts and their API</H2>
			{parts?.map(({ name, description, props, state, events, layout, accessibility }) => (
				<Part key={name} name={name} description={description} props={props} state={state} events={events} layout={layout} accessibility={accessibility} />
			))}
		</>
	)
}

export const Part = ({ name, description, props, state, events, layout, accessibility }: componentProps['parts'][0]) => {
	return (
		<>
			<H3>{name}</H3>
			<P>{description}</P>
			{props ?
				<>
					<H4>Props</H4>
					<div className="ring-opacity-5 mt-4 ring-1 shadow ring-black sm:rounded-lg">
						<table className="min-w-full divide-y divide-gray-300">
							<thead className="bg-gray-50">
								<tr>
									<th scope="col" className="py-3.5 pr-3 pl-4 text-left text-sm font-semibold text-gray-900 sm:pl-6">
										Prop
									</th>
									<th scope="col" className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
										Type
									</th>
									<th scope="col" className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
										Default
									</th>
									<th scope="col" className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
										Description
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-gray-200 bg-white">
								{props.map(({ name, type, default: defaultValue, description }) => (
									<tr key={name}>
										<td className="py-4 pr-3 pl-4 text-sm font-medium text-gray-900 sm:pl-6">{name}</td>
										<td className="py-4 px-3 text-sm text-gray-500">{type}</td>
										<td className="py-4 px-3 text-sm text-gray-500">{defaultValue}</td>
										<td className="py-4 px-3 text-sm text-gray-500">{description}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</>
			:	null}
			{state ?
				<>
					<H4>State</H4>
					<div className="ring-opacity-5 mt-4 ring-1 shadow ring-black sm:rounded-lg">
						<table className="min-w-full divide-y divide-gray-300">
							<thead className="bg-gray-50">
								<tr>
									<th scope="col" className="py-3.5 pr-3 pl-4 text-left text-sm font-semibold text-gray-900 sm:pl-6">
										Render Prop
									</th>
									<th scope="col" className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
										CSS Selector
									</th>
									<th scope="col" className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
										Description
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-gray-200 bg-white">
								{state.map(({ renderProp, CSSSelector, description }) => (
									<tr key={renderProp}>
										<td className="py-4 pr-3 pl-4 text-sm font-medium text-gray-900 sm:pl-6">{renderProp}</td>
										<td className="py-4 px-3 text-sm text-gray-500">{CSSSelector}</td>
										<td className="py-4 px-3 text-sm text-gray-500">{description}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</>
			:	null}
			{events ?
				<>
					<H4>Events</H4>
					<div className="ring-opacity-5 mt-4 ring-1 shadow ring-black sm:rounded-lg">
						<table className="min-w-full divide-y divide-gray-300">
							<thead className="bg-gray-50">
								<tr>
									<th scope="col" className="py-3.5 pr-3 pl-4 text-left text-sm font-semibold text-gray-900 sm:pl-6">
										Name
									</th>
									<th scope="col" className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
										Type
									</th>
									<th scope="col" className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
										Description
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-gray-200 bg-white">
								{events.map(({ name, type, description }) => (
									<tr key={name}>
										<td className="py-4 pr-3 pl-4 text-sm font-medium text-gray-900 sm:pl-6">{name}</td>
										<td className="py-4 px-3 text-sm text-gray-500">{type}</td>
										<td className="py-4 px-3 text-sm text-gray-500">{description}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</>
			:	null}
			{layout ?
				<>
					<H4>Layout</H4>
					<div className="ring-opacity-5 mt-4 ring-1 shadow ring-black sm:rounded-lg">
						<table className="min-w-full divide-y divide-gray-300">
							<thead className="bg-gray-50">
								<tr>
									<th scope="col" className="py-3.5 pr-3 pl-4 text-left text-sm font-semibold text-gray-900 sm:pl-6">
										Name
									</th>
									<th scope="col" className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
										Type
									</th>
									<th scope="col" className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
										Description
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-gray-200 bg-white">
								{layout.map(({ name, type, description }) => (
									<tr key={name}>
										<td className="py-4 pr-3 pl-4 text-sm font-medium text-gray-900 sm:pl-6">{name}</td>
										<td className="py-4 px-3 text-sm text-gray-500">{type}</td>
										<td className="py-4 px-3 text-sm text-gray-500">{description}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</>
			:	null}
			{accessibility ?
				<>
					<H4>Accessibility</H4>
					<div className="ring-opacity-5 mt-4 ring-1 shadow ring-black sm:rounded-lg">
						<table className="min-w-full divide-y divide-gray-300">
							<thead className="bg-gray-50">
								<tr>
									<th scope="col" className="py-3.5 pr-3 pl-4 text-left text-sm font-semibold text-gray-900 sm:pl-6">
										Name
									</th>
									<th scope="col" className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
										Type
									</th>
									<th scope="col" className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
										Description
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-gray-200 bg-white">
								{accessibility.map(({ name, type, description }) => (
									<tr key={name}>
										<td className="py-4 pr-3 pl-4 text-sm font-medium text-gray-900 sm:pl-6">{name}</td>
										<td className="py-4 px-3 text-sm text-gray-500">{type}</td>
										<td className="py-4 px-3 text-sm text-gray-500">{description}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</>
			:	null}
		</>
	)
}

export const Usage = ({ usage, shortName }: { usage: componentProps['usage']; shortName: componentProps['shortName'] }) => {
	return (
		<>
			<H2>Usage</H2>
			<CodeBlock code={usage} filename={`${shortName}Example`} extension="tsx" className="mt-4" />
			<P className="mt-4">
				Here's the <Code>{`<${shortName} />`}</Code> component in action.
			</P>
		</>
	)
}

export const Source = ({ source, fileName }: { source: string | null; fileName: componentProps['fileName'] }) => {
	return (
		<>
			<H2>Source</H2>
			{source ?
				<CodeBlock code={source} filename={fileName} extension="tsx" className="mt-4" />
			:	<div className="my-6">
					<NavLink
						prefetch="intent"
						to="/create-account"
						className="after:transition-max-width bg-brand-bg text-foreground after:bg-brand hover:bg-brand-bg-hover focus-visible:outline-brand-bg-hover relative rounded-md py-7 px-9 text-sm font-semibold shadow-sm after:absolute after:right-0 after:bottom-0 after:left-0 after:block after:h-0.5 after:max-w-0 after:duration-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 aria-[current]:after:max-w-full"
					>
						Get all-access
					</NavLink>
				</div>
			}
		</>
	)
}

export const Styling = ({ styling }: { styling: componentProps['styling'] }) => {
	return styling ?
			<>
				<H2>Styling</H2>
				<CodeBlock code={styling} filename="tailwind" extension="css" className="mt-4" />
			</>
		:	null
}
